import { useNavigate, useOutletContext } from 'react-router-dom'

import { Section } from '@factorialco/deprecated-design-system'
import { AddPeriodPageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import AddOrEditPeriodModal from 'modules/work_schedules/components/AddOrEditPeriodModal'
import type { RouteContextParams } from 'modules/work_schedules/lib/RouteContextParams'
import { PeriodScheduleType } from 'modules/work_schedules/lib/forms/PeriodScheduleTypeForm'
import OverlapPeriod, { ScheduleType } from 'modules/work_schedules/store/models/OverlapPeriod'

const AddPeriodPage = () => {
  const { navigation } = useNavigation()

  const { id } = useStrictParams<'id'>()
  const scheduleId = Number(id)
  const { workSchedule, overlapPeriods } = useOutletContext<RouteContextParams>()
  const defaultPeriod = workSchedule
    ? overlapPeriods.getDefaultPeriod(workSchedule?.attributes.id)
    : undefined

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data, loading } = useQuery(AddPeriodPageDocument, {
    variables: {
      scheduleId,
      companyId,
    },
  })

  const scheduleType = defaultPeriod
    ? defaultPeriod.attributes.schedule_type
    : PeriodScheduleType.flexible

  const currentYear = moment().year()
  const startDate = `${currentYear}-01-01`
  const endDate = `${currentYear}-12-31`

  const period = new OverlapPeriod({
    id: scheduleId,
    schedule_id: scheduleId,
    default: false,
    start_date: startDate,
    end_date: endDate,
    schedule_type: scheduleType as ScheduleType,
  })

  const navigate = useNavigate()

  const workScheduleRoute = navigation.settings.workSchedule.id.fullPath({ id })

  const onClose = () => {
    navigate(workScheduleRoute)
  }

  const onSuccess = () => {
    navigate(workScheduleRoute)
  }

  const canAssignEmployees = data?.workSchedule.schedule?.canAssignEmployeesPolicy.can ?? false
  const canReassignAll = data?.workSchedule.schedule?.canReassignEmployeesPolicy.can ?? false
  const canAddBreaks = data?.features.featuresConnection.nodes[0]?.enabled ?? false

  if (loading) {
    return <Loading />
  }

  return (
    <ResponsivePage.Body showHeader={false} variant="wide" loading={loading} error={null}>
      <Section.TwoThirds
        illustration="calendar-clock"
        title={i18n.t('settings.work_schedule.period_section.add_period_modal.title')}
        description={i18n.t('settings.work_schedule.period_section.add_period_modal.description')}
      >
        <AddOrEditPeriodModal
          onClose={onClose}
          onSuccess={onSuccess}
          id={Number(id)}
          period={period}
          add
          canAssignEmployees={canAssignEmployees}
          canReassignAll={canReassignAll}
          canAddBreaks={canAddBreaks}
        />
      </Section.TwoThirds>
    </ResponsivePage.Body>
  )
}

export default AddPeriodPage
